import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { useAuth } from '../context/AuthContext';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import ReplyAllOutlinedIcon from '@mui/icons-material/ReplyAllOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import './MainMenu.css';

const PICKING_SECTION_ITEMS = [
  { key: 'pick-list', name: 'Pick List', Icon: AssignmentOutlinedIcon, route: '/pick-list' },
  { key: 'pick-dashboard', name: 'Dashboard', Icon: SpaceDashboardOutlinedIcon, route: '/pick-list/dashboard' },
];

const buildCategories = ({ isAdmin, isMainStore, isPicker }) => {
  if (isPicker) {
    return [
      {
        id: 'picking',
        title: 'Picking',
        subtitle: 'Pick lists for internal fulfilment',
        items: [...PICKING_SECTION_ITEMS],
      },
    ];
  }

  const categories = [
    {
      id: 'sto',
      title: 'STO & stock transfer',
      subtitle: 'Import and manage stock transfer orders',
      items: [
        { key: 'sto-import', name: 'STO Import', Icon: UploadFileOutlinedIcon, route: '/sto-import' },
        { key: 'sto-list', name: 'STO List', Icon: ListAltOutlinedIcon, route: '/sto-list' },
      ],
    },
    {
      id: 'recall',
      title: 'Recall',
      subtitle: 'Upload recalls and view open recall lines',
      items: [
        { key: 'recall-import', name: 'Recall', Icon: ReplyOutlinedIcon, route: '/recall-import' },
        { key: 'recall-list', name: 'Recall List', Icon: ReplyAllOutlinedIcon, route: '/recall-list' },
      ],
    },
    {
      id: 'operations',
      title: 'Inventory & dispatch',
      subtitle: 'Stock on hand, deliveries, and reporting',
      items: [
        { key: 'inventory', name: 'Inventory', Icon: Inventory2OutlinedIcon, route: '/inventory' },
        { key: 'dispatch', name: 'Dispatch', Icon: LocalShippingOutlinedIcon, route: '/delivery' },
        { key: 'reports', name: 'Reports', Icon: AssessmentOutlinedIcon, route: '/reports' },
      ],
    },
  ];

  if (isMainStore) {
    categories.push({
      id: 'picking',
      title: 'Picking',
      subtitle: 'Main store pick lists',
      items: [...PICKING_SECTION_ITEMS],
    });
  }

  if (isAdmin) {
    categories.push({
      id: 'admin',
      title: 'Administration',
      subtitle: 'Locations, users and customer master',
      items: [
        { key: 'admin-locations', name: 'Consignment Locations', Icon: LocationOnOutlinedIcon, route: '/admin/locations' },
        { key: 'admin-users', name: 'Users', Icon: PeopleOutlinedIcon, route: '/admin/users' },
        { key: 'admin-customers', name: 'Customers', Icon: GroupsOutlinedIcon, route: '/admin/customers' },
      ],
    });
  }

  return categories;
};

const MainMenu = () => {
  const navigate = useNavigate();
  const { isAdmin, isMainStore, isPicker } = useAuth();

  const categories = useMemo(
    () => buildCategories({ isAdmin, isMainStore, isPicker }),
    [isAdmin, isMainStore, isPicker]
  );

  return (
    <Layout>
      <div className="home-menu-page">
        <header className="home-menu-header">
          <h1 className="home-menu-title">Home</h1>
        </header>

        <div
          className="home-menu-rail"
          role="region"
          aria-label="Module categories"
        >
          {categories.map((section) => (
            <section
              key={section.id}
              className="menu-category menu-category-column"
              aria-labelledby={`cat-${section.id}`}
            >
              <div className="menu-category-head">
                <h2 id={`cat-${section.id}`} className="menu-category-title">
                  {section.title}
                </h2>
                {section.subtitle && <p className="menu-category-subtitle">{section.subtitle}</p>}
              </div>
              <div className="menu-category-stack">
                {section.items.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className="menu-card menu-card-dark menu-card-home"
                    onClick={() => navigate(item.route)}
                  >
                    <span className="menu-card-icon-wrap" aria-hidden>
                      <item.Icon className="menu-card-mui-icon" />
                    </span>
                    <span className="menu-card-label">{item.name}</span>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default MainMenu;
